import React from 'react';
import { RQ_MERCHANT_RULES } from 'Utils';
import { Paper, Button } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { UseSplitViewForm } from 'Components/Table/hooks/useSplitViewForm';
import { shouldShowCreateRule } from 'Selectors';
import { RoleService } from 'Services';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { RulesTypes } from 'Common/enums';
import styles from './MerchantOverridePaper.css';
import DisplayContent from './DisplayContent';
import EditOverrideBtn from './EditOverrideBtn';
import RemoveOverrideBtn from './RemoveOverrideBtn';

interface Props extends Omit<UseSplitViewForm<MerchantRuleSplitViewFormState>, 'setRowId'> {
    paperClassName?: string;
    dataTestId: string;
    rule: Rule;
    readOnly: boolean;
    revision: number;
    selectedRowId: string;
    merchantId: string;
    mode?: string;
}

const MerchantOverridePaper = ({
    dataTestId,
    paperClassName,
    rule,
    readOnly,
    revision,
    selectedRowId,
    merchantId,
    mode
}: Props) => {
    const { t } = useTranslation();
    const isFetchingCount = useIsFetching([RQ_MERCHANT_RULES]);
    const isMutatingCount = useIsMutating();
    const history = useHistory();
    const isLoading = Boolean(isFetchingCount) || Boolean(isMutatingCount);
    const hasOverride = rule?.overrideGroup === t('Merchant Overrides');
    // The state of "createRule" feature flag will control user's edit access
    // forbid adhoc edit when the current rule is adhoc and createRule feature flag is off
    const forbidAdhocOverride = !!rule?.nonTemplated && !shouldShowCreateRule();
    const showRuleTest = mode === 'sandbox';
    const ruleType: RulesTypes = rule.expression ? RulesTypes.ADVANCED : RulesTypes.SIMPLE;

    const handleClick = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Subject-specific Override - View Rule Test',
                text: 'View Subject Override Rule Test'
            }
        });
        history.push(
            // eslint-disable-next-line max-len
            `/global-rules/rule-tester/view/${rule?.id}?merchant=${merchantId}&selectedRowId=${selectedRowId}`
        );
    };

    return (
        <Paper className={paperClassName}>
            <div className={styles.paperTitle}>
                {t('Merchant-Specific Override')}
                <div className={styles.buttonContainer}>
                    {hasOverride &&
                    RoleService.hasViewRuleTestRole &&
                    !RoleService.hasEditRuleOrBulkUploadAccess &&
                    showRuleTest ? (
                        <Button
                            className={styles.viewRuleTest}
                            dataTestId='view-rule-test'
                            variant='tertiary'
                            onClick={handleClick}
                            onKeyDown={handleClick}
                        >
                            {t('View Rule Test')}
                        </Button>
                    ) : null}
                    {forbidAdhocOverride ? null : (
                        <>
                            {hasOverride && (
                                <RemoveOverrideBtn
                                    id={rule?.id}
                                    showButton={!readOnly}
                                    revision={revision}
                                    ruleType={ruleType}
                                />
                            )}
                            <EditOverrideBtn
                                id={rule?.id}
                                showButton={!readOnly}
                                selectedRowId={selectedRowId}
                                hasOverride={hasOverride}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className={styles.merchantOverridePaper} data-testid={`${dataTestId}-override`}>
                {hasOverride && (
                    <DisplayContent
                        merchantSpecificSetting={rule?._formatted?.inputDescription}
                        overrideStatusBoolean={rule.parameters.active}
                        isLoading={isLoading}
                        overrideStatus={rule._formatted?.active as string}
                    />
                )}
            </div>
        </Paper>
    );
};

export default MerchantOverridePaper;
